import React, { useState } from "react";
import Loader from "../../../components/Loader";
import { formatToLocalDateTime } from "../utils/formatDate";
import { CustomModal } from "./CustomModal";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import { deleteTrainingVideo } from "../api/trainingVideos";

interface TrainingVideosTableProps {
  tableData: any;
  loading: boolean;
  refetch: boolean;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
}

const TrainingVideosTable: React.FC<TrainingVideosTableProps> = ({
  tableData,
  loading,
  refetch,
  setRefetch,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | number | null>(
    null
  );
  const navigate = useNavigate();

  const items = Array.isArray(tableData?.items)
    ? tableData.items
    : Array.isArray(tableData)
      ? tableData
      : [];

  const handleModalActivity = () => {
    setIsOpen(!isOpen);
  };

  const handleEdit = (id: string | number) => {
    navigate(URL_MAPPING.EDITTRAININGVIDEO(String(id)));
  };

  const handleDelete = async () => {
    try {
      if (selectedItemId === null) {
        toast.error("No training video selected for deletion");
        return;
      }

      const response = await deleteTrainingVideo(selectedItemId);
      if (!response) {
        toast.error("No data received or malformed response");
        return;
      }

      toast.success(response?.message || "Training video deleted successfully.");
      handleModalActivity();
      setRefetch(!refetch);
    } catch (error: any) {
      console.error("Failed to delete training video:", error);
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((err) => toast.error(err.msg));
      } else {
        toast.error(detail?.msg || detail || "Failed to delete training video.");
      }
    }
  };

  return (
    <>
      <ToastContainer />
      <CustomModal
        isOpen={isOpen}
        onClose={handleModalActivity}
        onDelete={handleDelete}
      />
      <div className="h-[calc(100vh-200px)] md:h-[calc(100vh-200px)] overflow-auto page-container p-2 pt-0 scrollbar scrollbar-thin">
        <table className="min-w-full bg-white border border-black-200 table-auto border-separate border-spacing-0">
          <thead className="bg-gray-200 sticky top-[0px] z-20">
            <tr>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                ID
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                Title
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                Start Date
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                End Date
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                Created At
              </th>
              <th className="border border-gray-300 text-13 font-bold text-black-500 whitespace-nowrap px-2 py-2 w-1">
                Actions
              </th>
            </tr>
          </thead>
          {loading ? (
            <tbody>
              <tr>
                <td colSpan={6} className="py-8">
                  <div className="flex justify-center items-center w-full">
                    <Loader />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : items.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={6} className="py-8 text-center text-gray-500">
                  No results found
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {items.map((item: any, index: number) => (
                <tr key={item.id ?? index} className="border-b text-sm">
                  <td className="border border-gray-300 px-4 py-2">
                    {item.id ?? "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {item.title || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {item.video_start_date || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {item.video_end_date || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {item.created_at
                      ? formatToLocalDateTime(item.created_at)
                      : "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(item.id)}
                        className="bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedItemId(item.id);
                          handleModalActivity();
                        }}
                        className="bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
    </>
  );
};

export default TrainingVideosTable;
